// ---------------------------------------------------------------------------
// Janela deslizante de velas (F3a C2): tamanho, despejo e pré-carga. Puro (testável em Node).
//
// A série aberta guarda no máximo `limit` velas brutas (padrão 10.000; ?janela= com ?dbg=1). Ao passar disso, sai um
// múltiplo de BLOCK (blocos inteiros) do lado MAIS DISTANTE da vista, nunca a vista nem uma folga de KEEP velas em
// volta dela. O lado do presente pode sair: a janela deixa de conter o presente e as velas novas ao vivo vão para a
// cauda (C3).
//
// Pré-carga: na direção do movimento, ao menos AHEAD velas carregadas à frente da vista (3 blocos; a força muda menos
// de ~7% a 3.000 velas ou mais da borda); parado, AHEAD_IDLE (mais 2 blocos) na última direção; do outro lado, ao
// menos KEEP (se a janela não chega ao presente).
// ---------------------------------------------------------------------------

export const BLOCK = 1000;
export const WINDOW_DEFAULT = 10000;
export const WINDOW_MIN = 8000;
export const WINDOW_MAX = 50000;
export const AHEAD = 3000;
export const AHEAD_IDLE = 5000;
/** folga mínima mantida dos dois lados da vista no despejo (e o mínimo carregado do lado oposto ao movimento) */
export const KEEP = 2000;

/** Tamanho da janela: ?janela=N só com o diagnóstico ligado; fora de [WINDOW_MIN, WINDOW_MAX] é limitado. */
export function windowLimitFrom(search: string, dbg: boolean): number {
  if (!dbg) return WINDOW_DEFAULT;
  let v = NaN;
  try { v = Number(new URLSearchParams(search).get('janela')); } catch { /* sem parâmetro */ }
  if (!Number.isFinite(v) || v <= 0) return WINDOW_DEFAULT;
  return Math.min(WINDOW_MAX, Math.max(WINDOW_MIN, Math.round(v)));
}

export interface EvictionPlan { side: 'oldest' | 'newest' | null; count: number }

/**
 * Quanto despejar e de que lado. `len` velas na janela, índices locais 0 (mais antiga) .. len-1 (mais nova);
 * [lo, hi] = trecho que tem de ficar (vista e alvo, em índices locais). Sai o lado mais distante de [lo, hi], em
 * blocos inteiros, sem chegar a menos de KEEP velas de [lo, hi]. Sem lugar para despejar: nada sai (a janela passa
 * do limite até a vista se afastar).
 */
export function evictionPlan(len: number, limit: number, lo: number, hi: number, keepSide: 'oldest' | 'newest' | null = null): EvictionPlan {
  const excess = len - limit;
  if (excess <= 0) return { side: null, count: 0 };
  lo = Math.max(0, Math.min(len - 1, Math.floor(lo)));
  hi = Math.max(lo, Math.min(len - 1, Math.ceil(hi)));
  const toOldest = lo, toNewest = len - 1 - hi;
  // o lado que acabou de ser carregado nunca sai (sem isso o bloco novo podia sair na hora e a carga se repetia)
  const side: 'oldest' | 'newest' = keepSide === 'oldest' ? 'newest' : keepSide === 'newest' ? 'oldest' : (toOldest >= toNewest ? 'oldest' : 'newest');
  const room = (side === 'oldest' ? toOldest : toNewest) - KEEP;
  if (room <= 0) return { side: null, count: 0 };
  const want = Math.ceil(excess / BLOCK) * BLOCK;
  const count = Math.min(want, Math.floor(room / BLOCK) * BLOCK || room);
  return count > 0 ? { side, count } : { side: null, count: 0 };
}

export type Direction = -1 | 0 | 1;   // -1 = para o passado, +1 = para o presente, 0 = sem direção ainda

export interface PreloadNeed { older: boolean; newer: boolean; first: 'older' | 'newer' | null }

/**
 * O que pré-carregar. `olderAhead` = velas carregadas além da vista no sentido do passado; `newerAhead` = no sentido
 * do presente (Infinity se a janela contém o presente). `olderDone` = início real dos dados já carregado.
 */
export function preloadNeed(olderAhead: number, newerAhead: number, dir: Direction, idle: boolean, olderDone: boolean): PreloadNeed {
  const want = idle ? AHEAD_IDLE : AHEAD;
  const wantOlder = dir === 1 ? KEEP : want;          // parado sem direção: os dois lados como "à frente"
  const wantNewer = dir === -1 ? KEEP : want;
  const older = !olderDone && olderAhead < wantOlder;
  const newer = Number.isFinite(newerAhead) && newerAhead < wantNewer;
  let first: 'older' | 'newer' | null = null;
  if (older && newer) first = dir === 1 ? 'newer' : 'older';
  else if (older) first = 'older';
  else if (newer) first = 'newer';
  return { older, newer, first };
}

/**
 * `before` do pedido do próximo bloco MAIS NOVO (a interface da fonte só pede "antes de"): cobre (newest, newest + BLOCK·iv].
 * A fonte devolve as `limit` velas com abertura <= endTime (= before) e o cliente fica com as < before; por isso
 * before = newest + BLOCK·iv + 1 ms (com + (BLOCK+1)·iv a primeira vela depois da mais nova ficaria de fora).
 */
export function newerBlockBefore(newestTime: number, intervalMs: number): number {
  return newestTime + BLOCK * intervalMs + 1;
}

// ---------------------------------------------------------------------------
// Cauda ao vivo (F3a C3): com o presente fora da janela, as velas do WebSocket ficam numa cauda pequena e separada
// (a janela não cresce nem desloca a vista). Ao voltar, os blocos mais novos são pedidos até o começo da cauda e a
// cauda é costurada no fim: sem lacuna e sem vela repetida.
// ---------------------------------------------------------------------------

/** no máximo um bloco de velas na cauda (o resto vem da fonte na volta) */
export const TAIL_MAX = BLOCK;

/**
 * Vela do WebSocket na cauda (velas brutas [openTime, ...]). Mesma abertura da última = vela em formação (substitui);
 * mais nova = entra. Vela que pularia uma ou mais velas (reconexão) recomeça a cauda nela: a cauda é sempre contígua
 * e o que faltou vem da fonte na costura. Muda `tail` no lugar; devolve a cauda.
 */
export function tailPush(tail: any[][], candle: any[], intervalMs: number): any[][] {
  const last = tail.length ? tail[tail.length - 1] : null;
  if (last && candle[0] === last[0]) { tail[tail.length - 1] = candle; return tail; }
  if (last && candle[0] < last[0]) return tail;                       // vela velha: ignora
  if (last && candle[0] - last[0] > intervalMs * 1.5) tail.length = 0;  // pulou vela(s): recomeça contígua
  tail.push(candle);
  if (tail.length > TAIL_MAX) tail.splice(0, tail.length - TAIL_MAX);
  return tail;
}

/** `before` do próximo bloco mais novo com cauda: não passa do começo da cauda (a costura emenda a cauda inteira). */
export function newerBlockBeforeWithTail(newestTime: number, intervalMs: number, tailStart: number | null): number {
  const b = newerBlockBefore(newestTime, intervalMs);
  return tailStart === null ? b : Math.min(b, tailStart);
}

export interface SeriesCheck { n: number; nonIncreasing: number; duplicates: number; gaps: number }
/** Horários estritamente crescentes no passo do timeframe? (lacunas contadas à parte: podem ser reais da fonte) */
export function checkSeries(rows: readonly (readonly any[])[], intervalMs: number): SeriesCheck {
  let nonIncreasing = 0, duplicates = 0, gaps = 0;
  for (let i = 1; i < rows.length; i++) {
    const d = rows[i][0] - rows[i - 1][0];
    if (d === 0) duplicates++;
    else if (d < 0) nonIncreasing++;
    else if (d !== intervalMs) gaps++;
  }
  return { n: rows.length, nonIncreasing, duplicates, gaps };
}

/** O que o App precisa da janela para a costura (SeriesWindow cumpre). */
export interface AppendableWindow { readonly length: number; readonly newestTime: number; readonly intervalMs: number; append(rows: readonly any[]): number }

export interface NewerBlockResult { added: number; stitched: boolean; reachedPresent: boolean; tailLength: number }
/**
 * Aplica um bloco MAIS NOVO (`got`, pedido com `before` = newerBlockBeforeWithTail) e, se ele chegou ao começo da
 * cauda, costura a cauda inteira no fim (a cauda fica vazia). Sem cauda: chegou ao presente se a fonte não tem nada
 * mais novo, se o pedido passou do agora ou se a vela mais nova é a do agora. Muda `win` e `tail` no lugar.
 */
export function applyNewerBlock(win: AppendableWindow, tail: any[][], got: readonly any[][], before: number, now: number): NewerBlockResult {
  const tail0 = tail.length ? tail[0][0] : null;
  let added = got.length ? win.append(got) : 0;
  // costura quando o bloco chegou ao começo da cauda; se a fonte não tem nenhuma vela no trecho pedido (lacuna real
  // maior que um bloco, raríssimo), costura também, sem pedir em laço (a lacuna fica e checkSeries a conta)
  const stitched = tail0 !== null && (before >= tail0 || added === 0);
  const tailLength = tail.length;
  if (stitched) { added += win.append(tail); tail.length = 0; }
  const reachedPresent = stitched || (tail0 === null && (added === 0 || before > now || win.newestTime >= now - 2 * win.intervalMs));
  return { added, stitched, reachedPresent, tailLength };
}
